import { useCallback, useRef, useState } from "react";
import {
  useSuggestDocumentFieldsMutation,
  type SuggestDocType,
  type SuggestFieldsItem,
} from "@/shared/api/documentSuggest";

export type DocumentSuggestState = "idle" | "scanning" | "done" | "empty" | "error";

export interface DocumentSuggestResult {
  status: DocumentSuggestState;
  fields: Record<string, string> | null;
  items: SuggestFieldsItem[] | null;
}

interface UseDocumentSuggestResult {
  scan: (file: File, docType: SuggestDocType) => Promise<DocumentSuggestResult>;
  state: DocumentSuggestState;
  fields: Record<string, string>;
  items: SuggestFieldsItem[] | null;
  reset: () => void;
}

export function useDocumentSuggest(token?: string): UseDocumentSuggestResult {
  const [suggestDocumentFields] = useSuggestDocumentFieldsMutation();
  const [state, setState] = useState<DocumentSuggestState>("idle");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [items, setItems] = useState<SuggestFieldsItem[] | null>(null);
  const inFlightRef = useRef<ReturnType<typeof suggestDocumentFields> | null>(null);

  const reset = useCallback(function reset() {
    if (inFlightRef.current) {
      inFlightRef.current.abort();
      inFlightRef.current = null;
    }
    setState("idle");
    setFields({});
    setItems(null);
  }, []);

  const scan = useCallback(
    async function scan(file: File, docType: SuggestDocType): Promise<DocumentSuggestResult> {
      if (inFlightRef.current) {
        inFlightRef.current.abort();
        inFlightRef.current = null;
      }
      setState("scanning");
      const request = suggestDocumentFields({ file, doc_type: docType, token });
      inFlightRef.current = request;
      try {
        const response = await request.unwrap();
        if (inFlightRef.current !== request) return { status: "idle", fields: null, items: null };
        const result = response.fields ?? {};
        const responseItems = response.items ?? null;
        const hasFields = Object.keys(result).length > 0;
        const hasItems = !!responseItems && responseItems.length > 0;
        setFields(result);
        setItems(responseItems);
        const status = hasFields || hasItems ? "done" : "empty";
        setState(status);
        return {
          status,
          fields: hasFields ? result : null,
          items: hasItems ? responseItems : null,
        };
      } catch (err) {
        if (inFlightRef.current !== request) return { status: "idle", fields: null, items: null };
        const status = (err as { status?: number | string })?.status;
        if (status === 422) {
          setState("empty");
          return { status: "empty", fields: null, items: null };
        }
        setState("error");
        return { status: "error", fields: null, items: null };
      } finally {
        if (inFlightRef.current === request) inFlightRef.current = null;
      }
    },
    [suggestDocumentFields, token],
  );

  return { scan, state, fields, items, reset };
}
